<template>
  <div class="dept-quick" v-if="availableDepartments.length">
    <span class="dept-quick__label">{{ $t('filters.departments') }}</span>
    <button v-for="dept in availableDepartments" :key="dept" type="button"
            class="dept-quick__pill" :class="['dept-quick__pill--' + dept.toLowerCase(), { 'is-active': isDeptActive(dept) }]"
            @click="toggleDept(dept)">
      <i :class="isDeptActive(dept) ? 'fa-solid fa-check-circle' : 'fa-regular fa-circle'"></i>
      {{ $t(`businessUnitDepartments.${dept}`) }}
      <span class="dept-quick__count">{{ deptBuIds(dept).length }}</span>
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

/**
 * One-click department pills (same behavior as the Sales Dashboard's):
 * toggling a department ADDS/REMOVES every business unit of that type to the
 * bound BU filter — pure client-side sugar over the existing BU filter, so
 * every backend keeps working unchanged. v-model = the selected BU id array
 * (whatever the page's filter calls it: buIds / businessUnit).
 *
 * `single` mode (Command Center): v-model is ONE department string (or null) —
 * the pills look identical but act as a radio, clicking the active one clears it.
 */
export default {
  name: 'DeptQuickFilter',
  props: {
    // Selected business-unit ids (the page's BU filter) — or, in `single` mode,
    // the selected department string. Null tolerated.
    value: { type: [Array, String], default: null },
    // Radio behaviour on a single department value instead of BU-id toggling.
    single: { type: Boolean, default: false },
    // Optional org narrowing — only that org's units count/toggle.
    orgIds: { type: Array, default: null },
  },
  computed: {
    ...mapGetters('data', ['allBusinessUnits']),
    availableDepartments() {
      const present = new Set((this.allBusinessUnits || []).map(b => b.type).filter(Boolean));
      return ['SALES', 'RETENTION', 'LANDING'].filter(t => present.has(t));
    },
  },
  methods: {
    deptBuIds(dept) {
      let bus = (this.allBusinessUnits || []).filter(b => b.type === dept);
      const orgs = this.orgIds || [];
      if (orgs.length) {
        bus = bus.filter(b => orgs.includes(b.orgId));
      }
      return bus.map(b => b.id);
    },
    // A department reads as "on" when every one of its units is selected.
    isDeptActive(dept) {
      if (this.single) {
        return this.value === dept;
      }
      const ids = this.deptBuIds(dept);
      const sel = Array.isArray(this.value) ? this.value : [];
      return ids.length > 0 && ids.every(id => sel.includes(id));
    },
    toggleDept(dept) {
      if (this.single) {
        this.$emit('input', this.value === dept ? null : dept);
        return;
      }
      const ids = this.deptBuIds(dept);
      if (!ids.length) return;
      const sel = new Set(Array.isArray(this.value) ? this.value : []);
      if (this.isDeptActive(dept)) {
        ids.forEach(id => sel.delete(id));
      } else {
        ids.forEach(id => sel.add(id));
      }
      this.$emit('input', [...sel]);
    },
  },
};
</script>

<style lang="scss" scoped>
.dept-quick {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.6rem 0.85rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;

  &__label {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    outline: none;
    white-space: nowrap;
    transition: all 0.18s ease;

    &--sales:hover { background: rgba(16, 185, 129, 0.1); color: #10b981; }
    &--retention:hover { background: rgba(0, 123, 255, 0.1); color: #007BFF; }
    &--landing:hover { background: rgba(245, 158, 11, 0.12); color: #d97706; }

    &--sales.is-active {
      color: #fff;
      background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%);
      box-shadow: 0 6px 14px -6px rgba(16, 185, 129, 0.55);
    }
    &--retention.is-active {
      color: #fff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);
    }
    &--landing.is-active {
      color: #fff;
      background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 60%, #d97706 100%);
      box-shadow: 0 6px 14px -6px rgba(245, 158, 11, 0.55);
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
    font-size: 0.68rem;
    font-weight: 700;
  }
  &__pill.is-active .dept-quick__count { background: rgba(0, 0, 0, 0.16); }
}

/* Dark theme (body-level .dark-layout ancestor + scoped targets) */
.dark-layout .dept-quick {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);

  .dept-quick__label { border-right-color: rgba(255, 255, 255, 0.08); }
  .dept-quick__pill { color: #b4b7bd; }
  .dept-quick__count { background: rgba(255, 255, 255, 0.1); }
}
</style>
